import { useEffect, useRef, useState } from "react";

import type { Effect } from "../../api/projects";
import { effectsFromClip } from "../../preview/Compositor";
import { createProgram } from "../../preview/webgl";
import { FRAG, VERT } from "../../preview/shaders";
import { fetchCubeLut, uploadLut3D } from "../../preview/lut";

/**
 * Mini canvas que aplica una lista de Effect[] a una imagen de muestra
 * (gradiente de colores) y muestra el resultado. Reutiliza FRAG + VERT
 * del compositor principal: idéntica matemática que el preview real.
 */

interface Props {
  effects: Effect[];
  size?: number;
  label?: string;
  icon?: string;
  onPick?: () => void;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  title?: string;
}

// Singletons compartidos entre thumbs para reducir memoria
let _sampleTex: WebGLTexture | null = null;
let _lutCache = new Map<string, WebGLTexture>();
let _loadingLuts = new Set<string>();

function makeSampleTexture(gl: WebGL2RenderingContext): WebGLTexture {
  if (_sampleTex) return _sampleTex;
  const size = 96;
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = (y * size + x) * 4;
      // Gradiente cielo→tierra→retrato (azul, naranja, beige rosado)
      const fy = y / (size - 1);
      const fx = x / (size - 1);
      let r, g, b;
      if (fy < 0.4) {
        // cielo
        r = 80 + fx * 40;  g = 130 + fx * 60;  b = 200 - fy * 100;
      } else if (fy < 0.7) {
        // horizonte / paisaje
        const t = (fy - 0.4) / 0.3;
        r = 140 + t * 80;  g = 110 + t * 40;  b = 70 - t * 20;
      } else {
        // figura / piel
        r = 220 + Math.sin(fx * 6) * 20;
        g = 170 + Math.sin(fx * 6) * 10;
        b = 140;
      }
      data[i] = Math.min(255, r);
      data[i + 1] = Math.min(255, g);
      data[i + 2] = Math.min(255, b);
      data[i + 3] = 255;
    }
  }
  const tex = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, size, size, 0, gl.RGBA, gl.UNSIGNED_BYTE, data);
  _sampleTex = tex;
  return tex;
}

async function ensureLut(gl: WebGL2RenderingContext, file: string) {
  if (_lutCache.has(file)) return _lutCache.get(file)!;
  if (_loadingLuts.has(file)) return null;
  _loadingLuts.add(file);
  try {
    const lut = await fetchCubeLut(`/luts/${file}`);
    const tex = uploadLut3D(gl, lut);
    _lutCache.set(file, tex);
    return tex;
  } finally {
    _loadingLuts.delete(file);
  }
}

export default function EffectThumb({
  effects, size = 70, label, icon, onPick, draggable, onDragStart, title,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const programRef = useRef<WebGLProgram | null>(null);
  const [needsRedraw, setNeedsRedraw] = useState(0);
  const e = effectsFromClip(effects);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const gl = canvas.getContext("webgl2", { premultipliedAlpha: false, alpha: false });
    if (!gl) return;

    if (!programRef.current) {
      try {
        programRef.current = createProgram(gl, VERT, FRAG);
      } catch (err) {
        console.error("EffectThumb shader compile failed:", err);
        return;
      }
      const buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, 1,1]), gl.STATIC_DRAW);
      gl.useProgram(programRef.current);
      const aPos = gl.getAttribLocation(programRef.current, "a_pos");
      gl.enableVertexAttribArray(aPos);
      gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);
      gl.uniform1i(gl.getUniformLocation(programRef.current, "u_video"), 0);
      gl.uniform1i(gl.getUniformLocation(programRef.current, "u_lut"), 1);
    }
    const program = programRef.current;
    if (!program) return;

    // Sample texture (TEXTURE0)
    const tex = makeSampleTexture(gl);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, tex);

    // Set uniforms
    gl.useProgram(program);
    gl.uniform1f(gl.getUniformLocation(program, "u_exposure"), e.exposure);
    gl.uniform1f(gl.getUniformLocation(program, "u_contrast"), e.contrast);
    gl.uniform1f(gl.getUniformLocation(program, "u_saturation"), e.saturation);
    gl.uniform1f(gl.getUniformLocation(program, "u_temperature"), e.temperature);

    let useLut = false;
    if (e.lutFile) {
      const lutTex = _lutCache.get(e.lutFile);
      if (lutTex) {
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_3D, lutTex);
        useLut = true;
        gl.uniform1f(gl.getUniformLocation(program, "u_lutIntensity"), e.lutIntensity);
      } else {
        ensureLut(gl, e.lutFile).then(() => setNeedsRedraw((n) => n + 1));
      }
    }
    gl.uniform1i(gl.getUniformLocation(program, "u_useLut"), useLut ? 1 : 0);

    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effects, needsRedraw]);

  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      onClick={onPick}
      title={title || label}
      style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        gap: 4, padding: 6, borderRadius: 6,
        cursor: onPick ? "pointer" : (draggable ? "grab" : "default"),
        background: "var(--bg-elev-2)",
        border: "1px solid var(--border)",
        transition: "border-color 0.12s, transform 0.08s",
      }}
      onMouseEnter={(ev) => ev.currentTarget.style.borderColor = "#fb923c"}
      onMouseLeave={(ev) => ev.currentTarget.style.borderColor = "var(--border)"}
    >
      <div style={{ position: "relative", width: size, height: Math.round(size * 9 / 16) }}>
        <canvas
          ref={canvasRef}
          width={size} height={Math.round(size * 9 / 16)}
          style={{ borderRadius: 3, background: "#000",
                    pointerEvents: "none", display: "block",
                    width: "100%", height: "100%" }}
        />
        {icon && (
          <div style={{
            position: "absolute", top: 2, right: 4, fontSize: 12,
            textShadow: "0 1px 2px rgba(0,0,0,0.8)",
          }}>{icon}</div>
        )}
      </div>
      <span style={{ fontSize: 10, textAlign: "center", color: "var(--text)",
                     maxWidth: size + 8, lineHeight: 1.15 }}>
        {label}
      </span>
    </div>
  );
}
